import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  ChoosePlan,
  Complete,
  InviteMember,
  Tenant,
} from '@/component/Onboarding';
import { ONBOARDING_CONST } from '@/constant';
import { useTypedSearchParams } from '@/hook';
import { VBreadcrumbs, VFlex } from '@/package';
import { useOnboardingLayout } from '@/provider/layout';
import type { OnboardingForm, OnboardingSearchParams } from '@/type';

const titleStyle =
  'inline-flex w-[608px] items-center justify-center px-2 py-1 font-bold';

const OnboardingPage = () => {
  const { set, flush, get } = useTypedSearchParams<OnboardingSearchParams>();
  const { setRenderTitle } = useOnboardingLayout();
  const { watch } = useFormContext<OnboardingForm>();

  const stepEnabledList = [
    true,
    !!watch('tenantName'),
    !!watch('tier') && !!watch('tenantName'),
    !!watch('tier') && !!watch('tenantName'),
  ];

  const pathMapList = [
    {
      path: `1. Workspace setup`,
      component: <Tenant />,
      queryParam: ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.TENANT,
      title: (
        <div className={titleStyle}>
          <div className="text-headline-md">
            Please enter your information to get started!
          </div>
        </div>
      ),
    },
    {
      path: `2. Choose a Plan`,
      component: <ChoosePlan stepEnabled={stepEnabledList[1]} />,
      queryParam: ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.CHOOSE_PLAN,
      title: (
        <div className={titleStyle}>
          <div className="text-headline-md">
            Please select a plan to get started with VueX.
          </div>
        </div>
      ),
    },
    {
      path: `3. Invite Members`,
      component: <InviteMember stepEnabled={stepEnabledList[2]} />,
      queryParam: ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.INVITE_MEMBERS,
      title: (
        <div className={titleStyle}>
          <div className="text-headline-md">
            Invite members to join you on Vueron.
          </div>
        </div>
      ),
    },
    {
      path: `4. Complete!`,
      component: <Complete stepEnabled={stepEnabledList[3]} />,
      queryParam: ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.COMPLETE,
      title: (
        <div className={titleStyle}>
          <div className="text-headline-md">Your sign-up is complete 🎉</div>
        </div>
      ),
    },
  ] as const;
  const step = get('step');
  const pathMapListIndex = pathMapList.findIndex(
    ({ queryParam }) => queryParam === step,
  );
  const selectedPathMap = pathMapList.at(pathMapListIndex);

  const onClickpath = (item: { label: string; value: string }) => {
    const newPath = item.value;
    const newMap = pathMapList.find(({ path }) => path === newPath);
    if (!newMap) return;

    if (
      newMap.queryParam ===
        ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.CHOOSE_PLAN &&
      !watch('tenantName')
    )
      return;

    if (
      newMap.queryParam ===
        ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.INVITE_MEMBERS &&
      (!watch('tier') || !watch('tenantName'))
    )
      return;

    if (
      newMap.queryParam ===
      ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.COMPLETE
    )
      return;

    set('step', newMap.queryParam);
    flush();
  };

  useEffect(() => {
    if (step) return;

    set('step', pathMapList.at(0)!.queryParam);
    flush();
  }, []);

  useEffect(() => {
    setRenderTitle(() => {
      return (
        <VFlex direction="col" className="gap-5">
          <VFlex className="w-[608px] justify-center rounded-md border border-skyBlue-200  px-1 py-1">
            <VBreadcrumbs
              pathList={pathMapList.map(({ path }, index) => ({
                label: path,
                value: path,
                disabled: !stepEnabledList[index],
              }))}
              value={selectedPathMap?.path}
              onChange={onClickpath}
            />
          </VFlex>

          {selectedPathMap?.title}
        </VFlex>
      );
    });
  }, [pathMapListIndex]);

  return pathMapList[pathMapListIndex]?.component;
};

export default OnboardingPage;
